<script lang="ts" context="module">
  export type ButtonSizes = "small" | "medium" | "large";
  export type ButtonVariants =
    | "primary"
    | "secondary"
    | "tertiary"
    | "community"
    | "danger"
    | "active"
    | "system"
    | "theme-agnostic";

  export type ButtonHTMLType = "button" | "submit" | "reset";

  export const sizes = {
    small: "text-xs px-1 h-[20px] rounded",
    medium: "text-sm md:text-[13px] px-2 h-[28px] rounded-[5px]",
    large: "text-sm md:text-[13px] px-2.5 h-[36px] rounded-md",
  };

  export const sharedButtonClasses = cx(
    "transition-colors font-normal duration-100",
    "focus:outline-none focus-visible:ring",
    "flex items-center justify-center gap-2"
  );
</script>

<script lang="ts">
  import ButtonBase from "./internal/ButtonBase.svelte";
  import cx from "classnames";
  import { tooltip } from "app/actions/tooltip";
  import type { IconType } from "app/components/+shared/OVIcon/OVIcon";

  export let label: string;
  export let id: string | undefined = undefined;
  export let size: ButtonSizes = "medium";
  export let variant: ButtonVariants;
  export let iconLeft: IconType | undefined = undefined;
  export let iconRight: IconType | undefined = undefined;
  export let disabled: boolean = false;
  export let isFullWidth: boolean = false;
  export let htmlButtonType: ButtonHTMLType = "button";
  export let form: string | undefined = undefined;
  export let tooltipLabel: string | undefined = undefined;

  $: variants = {
    // MAIN VARIANTS
    primary: cx(
      "bg-white hover:bg-black/5 text-neutral-600 hover:text-neutral-600 border border-black/10 shadow-xs", // light mode styles
      "dark:bg-white/5 dark:hover:bg-white/10 dark:text-neutral-300 dark:hover:text-neutral-300 dark:border-white/5 dark:shadow-sm" // dark mode styles
    ),

    secondary: cx(
      "hover:bg-black/5 text-neutral-600 hover:text-neutral-600 border border-black/10", // light mode styles
      "dark:hover:bg-white/10 dark:text-neutral-300 dark:hover:text-neutral-300 dark:border-white/10" // dark mode styles
    ),

    tertiary: cx(
      "hover:bg-black/5 text-neutral-600 border-transparent hover:text-neutral-600", // light mode styles
      "dark:hover:bg-white/10 dark:text-neutral-400 dark:hover:text-neutral-300 border dark:border-transparent" // dark mode styles
    ),

    // OTHER VARIANTS
    danger: "bg-danger-red/10 hover:bg-danger-red/15 text-danger-red border border-danger-red/5",

    community:
      "bg-community-primary hover:bg-community-primary-variant text-community-text-primary shadow",

    system:
      "bg-indigo-500 hover:bg-indigo-600 dark:bg-indigo-600 hover:dark:bg-indigo-700 text-white",

    active: cx(
      "border",
      "bg-white text-neutral-600 border-black/10", // light mode styles
      "dark:bg-white/10 dark:text-neutral-300 dark:border-white/10" // dark mode styles
    ),

    // used for buttons that are theme agnostic, e.g. the "Close" icon button in the image lightbox
    "theme-agnostic":
      "bg-neutral-700/75 border-[0.5px] border-neutral-500 backdrop-blur-md text-neutral-100 hover:brightness-[1.3] brightness-105 hover:backdrop-contrast-150",
  };

  $: sharedLocalClasses = cx(
    variant === "system" ? "focus:ring-white" : "focus:ring-focused-blue",
    disabled && "opacity-25 cursor-not-allowed pointer-events-none",
    "whitespace-nowrap"
  );
</script>

<span use:tooltip={{ content: tooltipLabel }} class="contents">
  <ButtonBase
    {id}
    class={cx(
      variants[variant],
      sizes[size],
      sharedLocalClasses,
      sharedButtonClasses,
      isFullWidth ? "w-full" : "w-auto"
    )}
    {label}
    {disabled}
    {htmlButtonType}
    {form}
    {iconLeft}
    {iconRight}
    on:click
  >
    <!-- avoid using this, for icons there is already the appropriate icon props -->
    <svelte:fragment slot="left">
      <slot name="left" />
    </svelte:fragment>
  </ButtonBase>
</span>
